import { ReactNode, useEffect, useState } from 'react';

import { useDebouncedCallback } from '@mantine/hooks';
import classNames from 'classnames';

import { SortDownIcon, SortUpIcon } from '@anyx/shared-icons';


export type SortingType = 'OFF' | 'ASC' | 'DESC';

export type SortButtonProps = {
  disabled?: boolean;
  label?: ReactNode;
  sorting?: SortingType;
  align?: 'left' | 'right';
  disabledSorting?: SortingType[];
  onClick?: (sorting: SortingType) => void;
  debounceTime?: number;
};

export enum SortingState {
  ASC = 'ASC',
  DESC = 'DESC',
  OFF = 'OFF',
}

const getSymmetricDifference = <T,>(array1: T[], array2: T[]): T[] => {
  const set1 = new Set(array1);
  const set2 = new Set(array2);

  return [...array1, ...array2].filter(
    (item) => (set1.has(item) && !set2.has(item)) || (!set1.has(item) && set2.has(item))
  );
};

export const SortButton = ({
  disabled: disabledFromProps,
  label,
  align,
  sorting: sortingFromProps,
  disabledSorting = [],
  onClick,
  debounceTime = 500,
}: SortButtonProps) => {
  const sortingOrder = getSymmetricDifference<SortingType>(['OFF', 'ASC', 'DESC'], disabledSorting);
  const disabled = disabledFromProps || sortingOrder.length === 0;
  const [sorting, setSorting] = useState<SortingType>('OFF');

  const debouncedOnClick = useDebouncedCallback((nextSorting: SortingType) => {
    onClick?.(nextSorting);
  }, debounceTime);

  const getNextSorting = (sorting: SortingType = 'OFF') => {
    const currentIndex = sortingOrder.indexOf(sorting);

    return sortingOrder[(currentIndex + 1) % sortingOrder.length];
  };

  const handleClick = () => {
    const nextSorting = getNextSorting(sorting) || 'OFF';

    if (disabledSorting.includes(nextSorting)) return;

    setSorting(nextSorting);
    debouncedOnClick(nextSorting);
  };

  useEffect(() => {
    if (sortingFromProps) setSorting(sortingFromProps);
  }, [sortingFromProps]);

  return (
    <button
      data-sorting={sorting}
      data-disabled={disabled}
      data-align={align}
      className={classNames('flex items-center gap-2 border-0 bg-transparent', {
        'justify-end': align === 'right',
        'flex-row-reverse justify-start': align === 'left',
        'cursor-pointer': !disabled,
        'cursor-auto': disabled,
      })}
      onClick={disabled ? undefined : handleClick}
    >
      {label}
      {!disabled && (
        <span className="icons flex flex-col">
          <SortUpIcon
            data-active={sorting === 'ASC'}
            className="fill-gray-5 data-[active=true]:fill-gray-9 h-4 w-4"
          />
          <SortDownIcon
            data-active={sorting === 'DESC'}
            className="fill-gray-5 data-[active=true]:fill-gray-9 h-4 w-4"
          />
        </span>
      )}
    </button>
  );
};
